import { useEffect, useRef } from "react";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

interface BackgroundState {
  element: HTMLElement;
  inert: boolean;
  ariaHidden: string | null;
}

export function useModalFocusTrap<T extends HTMLElement>(initialSelector?: string) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const modal = ref.current;
    if (!modal) return;
    const modalElement = modal;
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const background = makeBackgroundInert(modalElement);

    function focusableElements(): HTMLElement[] {
      return Array.from(modalElement.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (element) =>
          element.getAttribute("aria-hidden") !== "true" &&
          element.closest("[aria-hidden='true'], [inert]") === null,
      );
    }

    function trapTab(event: KeyboardEvent) {
      if (event.key !== "Tab") return;
      const openModals = Array.from(document.querySelectorAll<HTMLElement>("[aria-modal='true']"));
      if (openModals.at(-1) !== modalElement) return;
      const elements = focusableElements();
      if (!elements.length) {
        event.preventDefault();
        modalElement.focus();
        return;
      }
      const first = elements[0]!;
      const last = elements.at(-1)!;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !modalElement.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !modalElement.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", trapTab, true);
    const initial = initialSelector
      ? modalElement.querySelector<HTMLElement>(initialSelector)
      : focusableElements()[0];
    (initial ?? modalElement).focus();
    return () => {
      document.removeEventListener("keydown", trapTab, true);
      restoreBackground(background);
      previousFocus?.focus();
    };
  }, [initialSelector]);

  return ref;
}

function makeBackgroundInert(modal: HTMLElement): BackgroundState[] {
  const states: BackgroundState[] = [];
  const containingModal = modal.parentElement?.closest<HTMLElement>("[aria-modal='true']") ?? null;
  let branch: HTMLElement | null = modal;
  while (branch.parentElement && branch.parentElement !== document.body) {
    const parent: HTMLElement = branch.parentElement;
    for (const sibling of Array.from(parent.children)) {
      if (!(sibling instanceof HTMLElement) || sibling === branch) continue;
      states.push({
        element: sibling,
        inert: sibling.inert,
        ariaHidden: sibling.getAttribute("aria-hidden"),
      });
      sibling.inert = true;
      sibling.setAttribute("aria-hidden", "true");
    }
    if (parent === containingModal) break;
    branch = parent;
  }
  return states;
}

function restoreBackground(states: BackgroundState[]) {
  for (const { element, inert, ariaHidden } of states.reverse()) {
    element.inert = inert;
    if (ariaHidden === null) element.removeAttribute("aria-hidden");
    else element.setAttribute("aria-hidden", ariaHidden);
  }
}
